import Button from '@root/core/src/components/button';
import PropTypes from '@root/vendor/prop-types';
import React, { useEffect, useState } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import useScript from '@root/gatsby-contentful-core/src/hooks/use-script';
import {
  BUTTON_BASE_STYLES,
  BUTTON_SIZE_REGULAR,
  BUTTON_SIZE_STYLES,
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_STYLES,
} from '@root/core/src/components/button/styles';
import { Colors, StyleSheet, Theme } from '@root/core/src/utils/styles';
import { P1 } from '@root/gatsby-contentful-core/src/components/text';
import { semiBold } from '@root/brand/src/assets/fonts/root-sans/emotion';

/*
Using an embedded Hubspot form for this project originally seemed like a good idea for a number of reasons.
Initially it only required a few lines of code and was editable without Engineering support from within Hubspot.
But then more and more requests/requirements came in that weren't supported by the simple embedded form,
so in the end we have this hacked together setup you see below.

I wanted to make note of this so that if we need to do more work in this file going forward we should
consider a refactor. Maybe build it as a React form that hits the Hubspot API.
*/

export default function PartnerPlatformLandingPageForm({ closeModal }) {
  const status = useScript('//js.hsforms.net/forms/v2.js');
  const [formIsLoaded, setFormIsLoaded] = useState(false);
  const [formIsSubmitted, setFormIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState(false);

  useEffect(() => {
    if (status === 'ready') {
      window.hbspt.forms.create({
        region: 'na1',
        portalId: '6215402',
        formId: 'ef700f6e-1834-4350-afa6-a7eaebce5bcf',
        target: '#hubspot-form-container',
      });
    }
  }, [status]);

  useEffect(() => {
    if (!formIsLoaded) { return; }

    const formFields = [...document.querySelectorAll('.hs-form-field')];

    formFields.forEach((field) => {
      const input = field.querySelector('.hs-input');
      const customErrorMessage = field.querySelector('.hs-field-desc');

      customErrorMessage.setAttribute('role', 'alert');

      const mutationObserver = new MutationObserver((mutationsList) => {
        const inputHasError = mutationsList[0].target.classList.contains('error');
        const customErrorIsActive = customErrorMessage.classList.contains('error');
        if (inputHasError && !customErrorIsActive) { customErrorMessage.classList.add('error'); }
        if (!inputHasError && customErrorIsActive) { customErrorMessage.classList.remove('error'); }
      });

      const observerOptions = { attributeFilter: ['class']};
      mutationObserver.observe(input, observerOptions);

      return () => mutationObserver.disconnect();
    });
  }, [formIsLoaded]);

  useEffect(() => {
    const updateFormState = (event) => {
      if (formIsSubmitted) { return; }

      if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormReady') {
        setFormIsLoaded(true);
      }

      if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormSubmitted') {
        setSubmittedEmail(Object.keys(event.source.hubspot._emailValidationAndResubCache)[0]);
        setFormIsSubmitted(true);
      }
    };

    window.addEventListener('message', updateFormState);
    return () => {
      window.removeEventListener('message', updateFormState);
    };
  }, [formIsSubmitted]);

  return (
    <>
      <div
        css={styles.formContainer}
        id={'hubspot-form-container'}
      />
      {formIsSubmitted && (
        <>
          <P1 css={styles.modalEmailString}>We’ll send information regarding your&nbsp;inquiry&nbsp;to <span css={styles.modalEmail}>{submittedEmail}</span></P1>
          <Button onClick={closeModal}>Okay</Button>
        </>
      )}
    </>
  );
}

PartnerPlatformLandingPageForm.propTypes = {
  closeModal: PropTypes.func,
};

const styles = StyleSheet.create({
  formContainer: {
    ...Responsive.md({
      minWidth: 340,
    }),
    '.hs-richtext h4': {
      paddingRight: 50,
      marginBottom: 6,
      fontSize: 24,
      lineHeight: 1.1,
    },
    '.hs-richtext p': {
      color: Colors.rootOrange(),
      fontSize: 18,
      marginBottom: 20,
    },
    '.hs-form-field': {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column-reverse',
      marginBottom: 16,
      '& > label': {
        position: 'absolute',
        top: 6,
        left: 14,
        color: Colors.gray60(),
        fontSize: 16,
      },
      '& .hs-field-desc': {
        display: 'none !important',
        color: Colors.error(),
        fontSize: 16,
      },
      '& .hs-field-desc.error': {
        display: 'block !important',
      },
      '.hs-form-required': {
        color: Colors.rootOrange(),
        marginLeft: 1,
      },
    },
    '.hs-input': {
      ...Theme.input(),
      width: '100%',
      padding: '28px 14px 10px',
      borderRadius: 6,
      border: `1px solid ${Colors.gray40()}`,
      caretColor: Colors.rootOrange(),
      transition: 'border-color 200ms ease-in',
      ':focus': {
        outline: 'none',
        borderColor: Colors.black(),
      },
      '&.error': { borderColor: Colors.error() },
    },
    'ul': { listStyle: 'none' },
    '.hs_error_rollup': { display: 'none' },
    '.hs-error-msgs': {
      display: 'none !important', // HS provides no way to modify error messages on a per field basis.
    },
    '.hs-button': {
      ...BUTTON_BASE_STYLES,
      ...BUTTON_SIZE_STYLES[BUTTON_SIZE_REGULAR],
      ...BUTTON_VARIANT_STYLES[BUTTON_VARIANT_PRIMARY],
    },
    '.submitted-message': {
      ...semiBold(),
      color: Colors.nearBlack(),
      fontSize: 28,
      letterSpacing: '-0.01em',
      lineHeight: '110%',
      textAlign: 'center',

      ...Responsive.md({
        fontSize: 32,
      }),
    },
  },
  modalEmailString: {
    margin: '12px 0 34px',
    textAlign: 'center',
  },
  modalEmail: {
    fontWeight: 'bold',
  },
});
